import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  LogOut, Store, Plus, Edit, Trash2, Users, 
  Package, ShoppingCart, DollarSign, Eye, X,
  User, Lock, BarChart3, Settings, ExternalLink,
  Ban, CheckCircle, AlertTriangle, Link, Copy, Globe,
  FileEdit, Save, ChevronDown, ChevronUp, MessageCircle, Send, AlertCircle, Wrench
} from 'lucide-react';
import { toast } from 'sonner';
import { playNotificationSound } from '../../utils/notificationSound';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminPanel = () => {
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showStoreDetails, setShowStoreDetails] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [storeStats, setStoreStats] = useState(null);
  const [storeAdmins, setStoreAdmins] = useState([]);
  const [storeProducts, setStoreProducts] = useState([]);
  const [showProducts, setShowProducts] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({ name: '', price: '', stock: '', cost_price: '' });
  const [editingStore, setEditingStore] = useState(null);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [token, setToken] = useState(null);
  const [tokenLoaded, setTokenLoaded] = useState(false);
  const [saRole, setSaRole] = useState('superadmin');

  // Initialize token from secure storage
  useEffect(() => {
    const stored = sessionStorage.getItem('nc_superadmin_token');
    const storedRole = sessionStorage.getItem('nc_superadmin_role');
    if (stored) {
      try { setToken(decodeURIComponent(atob(stored))); } catch { /* invalid */ }
    }
    if (storedRole) setSaRole(storedRole);
    setTokenLoaded(true);
  }, []);

  const showLogin = tokenLoaded && !token;
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [formData, setFormData] = useState({
    store_name: '',
    domain: '',
    admin_path: '',
    owner_email: '',
    owner_name: '',
    phone: '',
    address: '',
    plan: 'free'
  });
  const [adminFormData, setAdminFormData] = useState({
    username: '',
    password: '',
    role: 'admin'
  });
  const [saUsernameAvailable, setSaUsernameAvailable] = useState(null);
  const [saCheckingUsername, setSaCheckingUsername] = useState(false);

  // Landing editor state
  const [showLandingEditor, setShowLandingEditor] = useState(false);
  const [landingConfig, setLandingConfig] = useState(null);
  const [savingLanding, setSavingLanding] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState(null);

  // Messaging state
  const [showChat, setShowChat] = useState(false);
  const [chatStore, setChatStore] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});
  const chatEndRef = React.useRef(null);
  const prevUnreadRef = React.useRef({});

  // SA Users management state
  const [showSaUsers, setShowSaUsers] = useState(false);
  const [saUsers, setSaUsers] = useState([]);
  const [saUserForm, setSaUserForm] = useState({ username: '', password: '', role: 'control_ventas', allowed_stores: [] });
  const [creatingSaUser, setCreatingSaUser] = useState(false);
  const [editingSaStores, setEditingSaStores] = useState(null); // user ID being edited

  const isSuperAdmin = saRole === 'superadmin';

  useEffect(() => {
    if (token) {
      loadStores();
      loadUnreadCounts();
      const unreadInterval = setInterval(loadUnreadCounts, 6000);
      return () => clearInterval(unreadInterval);
    }
  }, [token]);

  const loadLandingConfig = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/landing-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLandingConfig(res.data);
      setShowLandingEditor(true);
    } catch (error) {
      toast.error('Error al cargar configuración de landing');
    }
  };

  const handleSaveLanding = async () => {
    setSavingLanding(true);
    try {
      await axios.put(`${API}/superadmin/landing-config`, landingConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Landing actualizada exitosamente');
    } catch (error) {
      toast.error('Error al guardar landing');
    } finally {
      setSavingLanding(false);
    }
  };

  const updateFeature = (idx, field, value) => {
    const updated = [...landingConfig.features];
    updated[idx] = { ...updated[idx], [field]: value };
    setLandingConfig({ ...landingConfig, features: updated });
  };

  const addFeature = () => {
    setLandingConfig({
      ...landingConfig,
      features: [...landingConfig.features, { title: '', desc: '' }]
    });
  };

  const removeFeature = (idx) => {
    setLandingConfig({
      ...landingConfig,
      features: landingConfig.features.filter((_, i) => i !== idx)
    });
  };

  const updatePlan = (idx, field, value) => {
    const updated = [...landingConfig.plans];
    updated[idx] = { ...updated[idx], [field]: value };
    setLandingConfig({ ...landingConfig, plans: updated });
  };

  const updatePlanFeature = (planIdx, featIdx, value) => {
    const updated = [...landingConfig.plans];
    const feats = [...updated[planIdx].features];
    feats[featIdx] = value;
    updated[planIdx] = { ...updated[planIdx], features: feats };
    setLandingConfig({ ...landingConfig, plans: updated });
  };

  const addPlanFeature = (planIdx) => {
    const updated = [...landingConfig.plans];
    updated[planIdx] = { ...updated[planIdx], features: [...updated[planIdx].features, ''] };
    setLandingConfig({ ...landingConfig, plans: updated });
  };

  const removePlanFeature = (planIdx, featIdx) => {
    const updated = [...landingConfig.plans];
    updated[planIdx] = { ...updated[planIdx], features: updated[planIdx].features.filter((_, i) => i !== featIdx) };
    setLandingConfig({ ...landingConfig, plans: updated });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/superadmin/login`, loginData);
      const newToken = response.data.access_token;
      const role = response.data.sa_role || 'superadmin';
      sessionStorage.setItem('nc_superadmin_token', btoa(encodeURIComponent(newToken)));
      sessionStorage.setItem('nc_superadmin_role', role);
      setToken(newToken);
      setSaRole(role);
      toast.success(`Bienvenido ${role === 'superadmin' ? 'Super Admin' : 'Control de Ventas'}`);
    } catch (error) {
      toast.error('Credenciales inválidas');
    }
  };

  const loadStores = async () => {
    try {
      const response = await axios.get(`${API}/stores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStores(response.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadStoreDetails = async (store) => {
    try {
      const [statsRes, adminsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/stores/${store.id}/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/stores/${store.id}/admins`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/settings?store_id=${store.id}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} }))
      ]);
      setStoreStats({ ...statsRes.data, tools_module_enabled: settingsRes.data?.tools_module_enabled || false });
      setStoreAdmins(adminsRes.data);
      setSelectedStore(store);
      setShowStoreDetails(true);
    } catch (error) {
      toast.error('Error al cargar datos de la tienda');
      console.error(error);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('nc_superadmin_token');
    sessionStorage.removeItem('nc_superadmin_role');
    setToken(null);
    setSaRole('superadmin');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStore) {
        // Only send fields that StoreUpdate accepts (exclude domain)
        const updateData = {};
        if (formData.store_name) updateData.store_name = formData.store_name;
        if (formData.admin_path) updateData.admin_path = formData.admin_path;
        if (formData.plan) updateData.plan = formData.plan;
        if (formData.owner_email) updateData.owner_email = formData.owner_email;
        if (formData.owner_name) updateData.owner_name = formData.owner_name;
        if (formData.phone) updateData.phone = formData.phone;
        if (formData.address) updateData.address = formData.address;
        
        await axios.put(`${API}/stores/${editingStore.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Tienda actualizada');
      } else {
        // Clean empty strings for creation
        const createData = {
          store_name: formData.store_name,
          domain: formData.domain,
          owner_email: formData.owner_email,
          plan: formData.plan || 'free'
        };
        if (formData.admin_path) createData.admin_path = formData.admin_path;
        if (formData.owner_name) createData.owner_name = formData.owner_name;
        if (formData.phone) createData.phone = formData.phone;
        if (formData.address) createData.address = formData.address;
        
        await axios.post(`${API}/stores`, createData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Tienda creada. Admin: admin / 1234`);
      }
      setShowModal(false);
      resetForm();
      loadStores();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        toast.error(detail.map(d => d.msg || d).join(', '));
      } else {
        toast.error('Error al guardar la tienda');
      }
    }
  };

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    if (saUsernameAvailable === false) {
      toast.error('Ese nombre de usuario ya está en uso. Elija otro.');
      return;
    }
    try {
      if (editingAdmin) {
        await axios.put(
          `${API}/stores/${selectedStore.id}/admins/${editingAdmin.id}`,
          adminFormData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Admin actualizado');
      } else {
        await axios.post(
          `${API}/stores/${selectedStore.id}/admins`,
          adminFormData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Admin creado');
      }
      setShowAdminModal(false);
      setEditingAdmin(null);
      setAdminFormData({ username: '', password: '', role: 'admin' });
      setSaUsernameAvailable(null);
      // Reload admins
      const adminsRes = await axios.get(`${API}/stores/${selectedStore.id}/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStoreAdmins(adminsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar admin');
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    setDeleteConfirm({ type: 'admin', id: adminId });
  };

  const checkSaUsername = async (uname) => {
    if (!uname || uname.trim().length < 3) { setSaUsernameAvailable(null); return; }
    if (editingAdmin && uname === editingAdmin.username) { setSaUsernameAvailable(true); return; }
    setSaCheckingUsername(true);
    try {
      const res = await axios.get(`${API}/public/check-username/${uname.trim()}`);
      setSaUsernameAvailable(res.data.available);
    } catch {
      setSaUsernameAvailable(null);
    } finally {
      setSaCheckingUsername(false);
    }
  };

  const handleDelete = async (storeId, storeName) => {
    setDeleteConfirm({ type: 'store', id: storeId, name: storeName });
  };

  const handleDeleteProduct = async (productId, productName) => {
    setDeleteConfirm({ type: 'product', id: productId, name: productName });
  };

  const confirmDeleteAction = async () => {
    if (!deleteConfirm) return;
    try {
      if (deleteConfirm.type === 'admin') {
        await axios.delete(`${API}/stores/${selectedStore.id}/admins/${deleteConfirm.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Admin eliminado');
        setStoreAdmins(storeAdmins.filter(a => a.id !== deleteConfirm.id));
      } else if (deleteConfirm.type === 'store') {
        await axios.delete(`${API}/stores/${deleteConfirm.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Tienda eliminada');
        loadStores();
      } else if (deleteConfirm.type === 'product') {
        await axios.delete(`${API}/superadmin/products/${deleteConfirm.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Producto eliminado');
        setStoreProducts(storeProducts.filter(p => p.id !== deleteConfirm.id));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
    setDeleteConfirm(null);
  };

  const handleToggleActive = async (storeId) => {
    try {
      const response = await axios.put(`${API}/stores/${storeId}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      loadStores();
      if (selectedStore?.id === storeId) {
        setSelectedStore({ ...selectedStore, is_active: response.data.is_active });
      }
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const loadStoreProducts = async (storeId) => {
    try {
      const response = await axios.get(`${API}/superadmin/stores/${storeId}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStoreProducts(response.data);
      setShowProducts(true);
    } catch (error) {
      toast.error('Error al cargar productos');
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name || '',
      price: product.price || '',
      stock: product.stock || 0,
      cost_price: product.cost_price || 0
    });
  };

  const handleSaveProduct = async () => {
    if (!editingProduct) return;
    try {
      await axios.put(`${API}/superadmin/products/${editingProduct.id}`, {
        name: productForm.name,
        price: parseFloat(productForm.price),
        stock: parseInt(productForm.stock),
        cost_price: parseFloat(productForm.cost_price || 0)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Producto actualizado');
      setEditingProduct(null);
      loadStoreProducts(selectedStore.id);
    } catch (error) {
      toast.error('Error al actualizar producto');
    }
  };

  const openEditModal = (store) => {
    setEditingStore(store);
    setFormData({
      store_name: store.store_name,
      domain: store.domain,
      admin_path: store.admin_path || '',
      owner_email: store.owner_email || '',
      owner_name: store.owner_name || '',
      phone: store.phone || '',
      address: store.address || '',
      plan: store.plan || 'free'
    });
    setShowModal(true);
  };

  const openEditAdminModal = (admin) => {
    setEditingAdmin(admin);
    setAdminFormData({
      username: admin.username,
      password: '',
      role: admin.role || 'admin'
    });
    setShowAdminModal(true);
  };

  const resetForm = () => {
    setEditingStore(null);
    setFormData({
      store_name: '',
      domain: '',
      admin_path: '',
      owner_email: '',
      owner_name: '',
      phone: '',
      address: '',
      plan: 'free'
    });
  };

  // ===== Messaging functions =====
  const loadUnreadCounts = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/messages-unread`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newCounts = res.data;
      // Play sound if any new unread appeared
      const prevTotal = Object.values(prevUnreadRef.current).reduce((a, b) => a + b, 0);
      const newTotal = Object.values(newCounts).reduce((a, b) => a + b, 0);
      if (newTotal > prevTotal && prevTotal > 0) {
        playNotificationSound('message');
      }
      prevUnreadRef.current = newCounts;
      setUnreadCounts(newCounts);
    } catch (e) { /* ignore */ }
  };

  const openChat = async (store) => {
    setChatStore(store);
    setShowChat(true);
    setChatInput('');
    try {
      const res = await axios.get(`${API}/superadmin/messages/${store.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatMessages(res.data);
      setUnreadCounts(prev => ({ ...prev, [store.id]: 0 }));
    } catch (e) {
      toast.error('Error al cargar mensajes');
    }
  };

  const refreshChat = async () => {
    if (!chatStore) return;
    try {
      const res = await axios.get(`${API}/superadmin/messages/${chatStore.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatMessages(res.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (showChat && chatStore) {
      const interval = setInterval(refreshChat, 4000);
      return () => clearInterval(interval);
    }
  }, [showChat, chatStore]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const sendChatMessage = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || !chatStore) return;
    setChatSending(true);
    try {
      await axios.post(`${API}/superadmin/messages/${chatStore.id}`, { message: chatInput.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatInput('');
      refreshChat();
    } catch (err) {
      toast.error('Error al enviar mensaje');
    } finally {
      setChatSending(false);
    }
  };

  const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0);

  // SA Users management functions
  const loadSaUsers = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setSaUsers(res.data);
    } catch { /* control_ventas can't load this */ }
  };

  const handleCreateSaUser = async (e) => {
    e.preventDefault();
    if (!saUserForm.username || !saUserForm.password) { toast.error('Complete todos los campos'); return; }
    setCreatingSaUser(true);
    try {
      await axios.post(`${API}/superadmin/users`, saUserForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Usuario '${saUserForm.username}' creado`);
      setSaUserForm({ username: '', password: '', role: 'control_ventas', allowed_stores: [] });
      loadSaUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear usuario'); }
    finally { setCreatingSaUser(false); }
  };

  const handleDeleteSaUser = async (userId, username) => {
    if (!window.confirm(`¿Eliminar usuario ${username}?`)) return;
    try {
      await axios.delete(`${API}/superadmin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Usuario eliminado');
      loadSaUsers();
    } catch { toast.error('Error al eliminar'); }
  };

  const toggleStoreForUser = (storeId) => {
    if (editingSaStores) {
      // Editing existing user
      const user = saUsers.find(u => u.id === editingSaStores);
      if (!user) return;
      const current = user.allowed_stores || [];
      const updated = current.includes(storeId) ? current.filter(s => s !== storeId) : [...current, storeId];
      setSaUsers(prev => prev.map(u => u.id === editingSaStores ? { ...u, allowed_stores: updated } : u));
    } else {
      // Creating new user
      const current = saUserForm.allowed_stores || [];
      const updated = current.includes(storeId) ? current.filter(s => s !== storeId) : [...current, storeId];
      setSaUserForm({ ...saUserForm, allowed_stores: updated });
    }
  };

  const saveUserStores = async (userId) => {
    const user = saUsers.find(u => u.id === userId);
    if (!user) return;
    try {
      await axios.put(`${API}/superadmin/users/${userId}`, { allowed_stores: user.allowed_stores || [] }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Tiendas actualizadas');
      setEditingSaStores(null);
    } catch { toast.error('Error al guardar'); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(typeof dateStr === 'string' && !dateStr.endsWith('Z') ? dateStr + 'Z' : dateStr);
    return d.toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Guayaquil' });
  };

  // Login Screen
  if (showLogin) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-section)' }}>
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full">
          <div className="text-center mb-6">
            <Store size={48} className="mx-auto text-accent-text mb-3" />
            <h1 className="heading-2">Panel Super Admin</h1>
            <p className="text-text-muted">Gestión de Tiendas</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Usuario</label>
              <input
                type="text"
                value={loginData.username}
                onChange={(e) => setLoginData({...loginData, username: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                style={{ borderColor: 'var(--border-light)' }}
                placeholder="superadmin"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Contraseña</label>
              <input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2"
                style={{ borderColor: 'var(--border-light)' }}
                placeholder="••••••••"
                required
              />
            </div>
            <button type="submit" className="btn-primary w-full">
              Ingresar
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-section)' }}>
      {/* Header */}
      <div className="bg-white border-b" style={{ borderColor: 'var(--border-light)' }}>
        <div className="container py-3 md:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Store className="text-accent-text flex-shrink-0" size={28} />
              <div className="min-w-0">
                <h1 className="text-base md:text-lg font-bold truncate">Panel Multi-Tienda</h1>
                <p className="text-xs text-text-muted">{isSuperAdmin ? 'Administración' : 'Control de Ventas'}</p>
              </div>
            </div>
            <div className="flex gap-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
              {isSuperAdmin && (
                <button onClick={loadLandingConfig} className="btn-secondary text-sm whitespace-nowrap flex-shrink-0" data-testid="edit-landing-btn">
                  <FileEdit size={16} className="mr-1.5" />
                  <span className="hidden sm:inline">Editar </span>Landing
                </button>
              )}
              {isSuperAdmin && (
                <button onClick={() => { setShowSaUsers(true); loadSaUsers(); }} className="btn-secondary text-sm whitespace-nowrap flex-shrink-0" data-testid="sa-users-btn">
                  <Users size={16} className="mr-1.5" />
                  <span className="hidden sm:inline">Usuarios</span>
                </button>
              )}
              <button onClick={() => {
                const storeWithUnread = stores.find(s => unreadCounts[s.id] > 0);
                if (storeWithUnread) openChat(storeWithUnread);
                else toast('Selecciona una tienda y haz clic en el icono de mensaje para chatear');
              }} className="btn-secondary text-sm relative whitespace-nowrap flex-shrink-0" data-testid="superadmin-messages-btn">
                <MessageCircle size={16} className="mr-1.5" />
                <span className="hidden sm:inline">Mensajes</span>
                {totalUnread > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                    {totalUnread}
                  </span>
                )}
              </button>
              <button onClick={handleLogout} className="btn-secondary text-sm whitespace-nowrap flex-shrink-0">
                <LogOut size={16} className="mr-1.5" />
                <span className="hidden sm:inline">Salir</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-4 md:py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
          <div className="bg-white p-5 rounded-xl border" style={{ borderColor: 'var(--border-light)' }}>
            <div className="flex items-center gap-3">
              <Store className="text-accent-text" size={24} />
              <div>
                <p className="text-sm text-text-muted">Total Tiendas</p>
                <p className="text-2xl font-bold">{stores.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border" style={{ borderColor: 'var(--border-light)' }}>
            <div className="flex items-center gap-3">
              <Package className="text-green-500" size={24} />
              <div>
                <p className="text-sm text-text-muted">Activas</p>
                <p className="text-2xl font-bold">{stores.filter(s => s.is_active !== false).length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border" style={{ borderColor: 'var(--border-light)' }}>
            <div className="flex items-center gap-3">
              <DollarSign className="text-blue-500" size={24} />
              <div>
                <p className="text-sm text-text-muted">Premium</p>
                <p className="text-2xl font-bold">{stores.filter(s => s.plan === 'premium').length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border" style={{ borderColor: 'var(--border-light)' }}>
            <div className="flex items-center gap-3">
              <Users className="text-purple-500" size={24} />
              <div>
                <p className="text-sm text-text-muted">Gratuitas</p>
                <p className="text-2xl font-bold">{stores.filter(s => s.plan === 'free').length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center mb-4 md:mb-6">
          <h2 className="text-base md:text-lg font-bold">Tiendas Registradas</h2>
          <button 
            onClick={() => { resetForm(); setShowModal(true); }} 
            className="btn-primary text-sm"
          >
            <Plus size={18} className="mr-1.5" />
            Nueva Tienda
          </button>
        </div>

        {/* Store List */}
        {loading ? (
          <div className="text-center py-12">
            <Store size={48} className="animate-spin mx-auto text-accent-text" />
          </div>
        ) : stores.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center" style={{ borderColor: 'var(--border-light)' }}>
            <Store size={64} className="mx-auto text-text-muted mb-4" />
            <h3 className="heading-4 mb-2">No hay tiendas</h3>
            <p className="text-text-muted mb-4">Crea tu primera tienda para comenzar</p>
            <button onClick={() => setShowModal(true)} className="btn-primary">
              <Plus size={20} className="mr-2" />
              Crear Tienda
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {stores.map((store) => (
              <div 
                key={store.id} 
                className="bg-white rounded-xl border overflow-hidden"
                style={{ borderColor: 'var(--border-light)' }}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-lg">{store.store_name}</h3>
                      <p className="text-sm text-accent-text">/{store.admin_path || `admin/${store.domain}`}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      store.plan === 'premium' ? 'bg-yellow-100 text-yellow-700' :
                      store.plan === 'basic' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {store.plan}
                    </span>
                  </div>
                  
                  {/* Public Store Link */}
                  <div className="mb-3 p-2 rounded-lg border flex items-center gap-2" style={{ borderColor: 'var(--border-light)', background: 'var(--accent-wash)' }}>
                    <Globe size={14} className="text-accent-text flex-shrink-0" />
                    <span className="text-xs text-accent-text font-mono truncate flex-1">
                      {window.location.origin}/tienda/{store.domain}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(`${window.location.origin}/tienda/${store.domain}`);
                        toast.success('Enlace copiado!');
                      }}
                      className="p-1 hover:bg-white rounded flex-shrink-0"
                      title="Copiar enlace público"
                      data-testid={`copy-link-${store.id}`}
                    >
                      <Copy size={14} className="text-accent-text" />
                    </button>
                  </div>

                  <div className="space-y-1 text-sm text-text-muted">
                    <p>{store.owner_email}</p>
                    {store.phone && <p>{store.phone}</p>}
                  </div>

                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    <span className={`w-2 h-2 rounded-full ${store.is_active !== false ? 'bg-green-500' : 'bg-red-500'}`}></span>
                    <span className="text-xs">{store.is_active !== false ? 'Activa' : 'Inactiva'}</span>
                    {store.created_at && (
                      <span className="text-xs text-text-muted ml-auto">Creada: {formatDate(store.created_at)}</span>
                    )}
                  </div>
                </div>

                <div className="border-t px-5 py-3 flex justify-between items-center" style={{ borderColor: 'var(--border-light)' }}>
                  <div className="flex gap-1">
                    <button
                      onClick={() => loadStoreDetails(store)}
                      className="p-2 hover:bg-accent-wash rounded-lg text-accent-text"
                      title="Ver detalles"
                    >
                      <BarChart3 size={16} />
                    </button>
                    <button
                      onClick={() => window.open(`/${store.admin_path || `admin/${store.domain}`}/dashboard`, '_blank')}
                      className="p-2 hover:bg-accent-wash rounded-lg text-accent-text"
                      title="Ir al admin"
                    >
                      <ExternalLink size={16} />
                    </button>
                    <button
                      onClick={() => openChat(store)}
                      className="p-2 hover:bg-blue-50 rounded-lg relative"
                      title="Enviar mensaje"
                      data-testid={`chat-store-${store.id}`}
                    >
                      <MessageCircle size={16} className="text-blue-500" />
                      {unreadCounts[store.id] > 0 && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                          {unreadCounts[store.id]}
                        </span>
                      )}
                    </button>
                    {isSuperAdmin && (
                      <button
                        onClick={() => handleToggleActive(store.id)}
                        className={`p-2 rounded-lg ${store.is_active !== false ? 'hover:bg-red-50' : 'hover:bg-green-50'}`}
                        title={store.is_active !== false ? 'Inhabilitar tienda' : 'Habilitar tienda'}
                        data-testid={`toggle-active-${store.id}`}
                      >
                        {store.is_active !== false 
                          ? <Ban size={16} className="text-red-500" />
                          : <CheckCircle size={16} className="text-green-500" />
                        }
                      </button>
                    )}
                  </div>
                  {isSuperAdmin && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => openEditModal(store)}
                        className="p-2 hover:bg-accent-wash rounded-lg"
                        title="Editar"
                      >
                        <Edit size={16} className="text-accent-text" />
                      </button>
                      <button
                        onClick={() => handleDelete(store.id, store.store_name)}
                        className="p-2 hover:bg-red-50 rounded-lg"
                        title="Eliminar"
                      >
                        <Trash2 size={16} className="text-red-500" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Store Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center" style={{ borderColor: 'var(--border-light)' }}>
              <h2 className="heading-4">
                {editingStore ? 'Editar Tienda' : 'Nueva Tienda'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-section rounded-lg">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Nombre de la Tienda *</label>
                  <input
                    type="text"
                    value={formData.store_name}
                    onChange={(e) => setFormData({...formData, store_name: e.target.value})}
                    className="w-full px-4 py-2 rounded-lg border"
                    style={{ borderColor: 'var(--border-light)' }}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Dominio *</label>
                  <div className="flex">
                    <input
                      type="text"
                      value={formData.domain}
                      onChange={(e) => setFormData({...formData, domain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                      className="w-full px-4 py-2 rounded-l-lg border"
                      style={{ borderColor: 'var(--border-light)' }}
                      placeholder="mitienda"
                      required
                      disabled={editingStore}
                    />
                    <span className="px-3 py-2 bg-section border border-l-0 rounded-r-lg text-sm text-text-muted">
                      .com
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Ruta Admin Personalizada *</label>
                  <div className="flex">
                    <span className="px-3 py-2 bg-section border border-r-0 rounded-l-lg text-sm text-text-muted">
                      /
                    </span>
                    <input
                      type="text"
                      value={formData.admin_path}
                      onChange={(e) => setFormData({...formData, admin_path: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                      className="w-full px-4 py-2 rounded-r-lg border"
                      style={{ borderColor: 'var(--border-light)' }}
                      placeholder="ej: admin123, panelventas"
                      data-testid="store-admin-path-input"
                    />
                  </div>
                  <p className="text-xs text-text-muted mt-1">URL de acceso al panel de admin de esta tienda</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Plan</label>
                  <select
                    value={formData.plan}
                    onChange={(e) => setFormData({...formData, plan: e.target.value})}
                    className="w-full px-4 py-2 rounded-lg border"
                    style={{ borderColor: 'var(--border-light)' }}
                  >
                    <option value="free">Gratuito</option>
                    <option value="basic">Básico</option>
                    <option value="premium">Premium</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Email del Dueño *</label>
                  <input
                    type="email"
                    value={formData.owner_email}
                    onChange={(e) => setFormData({...formData, owner_email: e.target.value})}
                    className="w-full px-4 py-2 rounded-lg border"
                    style={{ borderColor: 'var(--border-light)' }}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Nombre del Dueño</label>
                  <input
                    type="text"
                    value={formData.owner_name}
                    onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
                    className="w-full px-4 py-2 rounded-lg border"
                    style={{ borderColor: 'var(--border-light)' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Teléfono</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-2 rounded-lg border"
                    style={{ borderColor: 'var(--border-light)' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Dirección</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    className="w-full px-4 py-2 rounded-lg border"
                    style={{ borderColor: 'var(--border-light)' }}
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">
                  Cancelar
                </button>
                <button type="submit" className="btn-primary flex-1">
                  {editingStore ? 'Guardar Cambios' : 'Crear Tienda'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Store Details Modal */}
      {showStoreDetails && selectedStore && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center" style={{ borderColor: 'var(--border-light)' }}>
              <div>
                <h2 className="heading-4">{selectedStore.store_name}</h2>
                <p className="text-sm text-text-muted">/{selectedStore.admin_path || `admin/${selectedStore.domain}`}</p>
              </div>
              <button onClick={() => setShowStoreDetails(false)} className="p-2 hover:bg-section rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              {/* Stats Grid */}
              {storeStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 rounded-lg" style={{ background: 'var(--bg-section)' }}>
                    <p className="text-xs text-text-muted">Productos</p>
                    <p className="text-xl font-bold">{storeStats.products_count}</p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ background: 'var(--bg-section)' }}>
                    <p className="text-xs text-text-muted">Stock Total</p>
                    <p className="text-xl font-bold">{storeStats.total_stock}</p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ background: 'var(--bg-section)' }}>
                    <p className="text-xs text-text-muted">Inversión</p>
                    <p className="text-xl font-bold text-blue-600">${storeStats.total_investment}</p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ background: 'var(--bg-section)' }}>
                    <p className="text-xs text-text-muted">Ingresos Totales</p>
                    <p className="text-xl font-bold text-green-600">${storeStats.total_revenue}</p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ background: 'var(--bg-section)' }}>
                    <p className="text-xs text-text-muted">Pedidos Online</p>
                    <p className="text-xl font-bold">{storeStats.orders_count}</p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ background: 'var(--bg-section)' }}>
                    <p className="text-xs text-text-muted">Ventas en Local</p>
                    <p className="text-xl font-bold">{storeStats.sales_count}</p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ background: 'var(--bg-section)' }}>
                    <p className="text-xs text-text-muted">Ganancia POS</p>
                    <p className="text-xl font-bold text-green-600">${storeStats.sales_profit}</p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ background: 'var(--bg-section)' }}>
                    <p className="text-xs text-text-muted">Categorías</p>
                    <p className="text-xl font-bold">{storeStats.categories_count}</p>
                  </div>
                </div>
              )}

              {/* Modules Toggle */}
              {isSuperAdmin && (
                <div className="border-t pt-6 mb-6" style={{ borderColor: 'var(--border-light)' }}>
                  <h3 className="font-bold flex items-center gap-2 mb-3">
                    <Wrench size={20} className="text-accent-text" />
                    Módulos
                  </h3>
                  <div className="flex items-center justify-between p-3 rounded-lg border" style={{ borderColor: 'var(--border-light)' }}>
                    <div>
                      <p className="font-medium text-sm">Herramientas y Activos</p>
                      <p className="text-xs text-text-muted">Préstamo y devolución de herramientas/equipos</p>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          const res = await axios.put(`${API}/superadmin/stores/${selectedStore.id}/modules`, {
                            module: 'tools_module', enabled: !storeStats?.tools_module_enabled
                          }, { headers: { Authorization: `Bearer ${token}` } });
                          toast.success(res.data.message);
                          loadStoreDetails(selectedStore);
                        } catch (e) { toast.error('Error al cambiar módulo'); }
                      }}
                      className={`relative w-12 h-6 rounded-full transition-colors ${storeStats?.tools_module_enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                      data-testid="toggle-tools-module">
                      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${storeStats?.tools_module_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
              )}

              {/* Admins Section */}
              <div className="border-t pt-6" style={{ borderColor: 'var(--border-light)' }}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-bold flex items-center gap-2">
                    <Users size={20} className="text-accent-text" />
                    Administradores de la Tienda
                  </h3>
                  {isSuperAdmin && (
                    <button
                      onClick={() => {
                        setEditingAdmin(null);
                        setAdminFormData({ username: '', password: '', role: 'admin' });
                        setShowAdminModal(true);
                      }}
                      className="btn-primary text-sm"
                    >
                      <Plus size={16} className="mr-1" />
                      Nuevo Admin
                    </button>
                  )}
                </div>

                {storeAdmins.length === 0 ? (
                  <p className="text-text-muted text-center py-4">No hay administradores</p>
                ) : (
                  <div className="space-y-2">
                    {storeAdmins.map(admin => (
                      <div 
                        key={admin.id}
                        className="flex items-center justify-between p-3 rounded-lg border"
                        style={{ borderColor: 'var(--border-light)' }}
                      >
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="w-10 h-10 rounded-full bg-accent-wash flex items-center justify-center">
                            <User size={20} className="text-accent-text" />
                          </div>
                          <div>
                            <p className="font-medium">{admin.username}</p>
                            <p className="text-xs text-text-muted">
                              {admin.role === 'admin' ? 'Administrador' : admin.role === 'empleado' ? 'Empleado' : admin.role === 'inventario' ? 'Inventario' : admin.role === 'mesera' ? 'Mesera' : admin.role}
                            </p>
                          </div>
                          {isSuperAdmin && (
                            <div className="ml-2 px-3 py-1 rounded-lg bg-gray-50 border" style={{ borderColor: 'var(--border-light)' }}>
                              <p className="text-xs text-text-muted">Contraseña</p>
                              <p className="text-sm font-mono font-medium" data-testid={`admin-password-${admin.id}`}>
                                {admin.plain_password || '••••••'}
                              </p>
                            </div>
                          )}
                          {admin.created_at && (
                            <div className="ml-2 px-3 py-1 rounded-lg bg-blue-50 border border-blue-100">
                              <p className="text-xs text-text-muted">Creado</p>
                              <p className="text-xs font-medium">{formatDate(admin.created_at)}</p>
                            </div>
                          )}
                        </div>
                        {isSuperAdmin && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => openEditAdminModal(admin)}
                              className="p-2 hover:bg-accent-wash rounded-lg"
                              title="Editar"
                            >
                              <Edit size={16} className="text-accent-text" />
                            </button>
                            <button
                              onClick={() => handleDeleteAdmin(admin.id)}
                              className="p-2 hover:bg-red-50 rounded-lg"
                              title="Eliminar"
                            >
                              <Trash2 size={16} className="text-red-500" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              <div className="border-t pt-6 mt-6 flex gap-3 flex-wrap" style={{ borderColor: 'var(--border-light)' }}>
                <button
                  onClick={() => window.open(`/${selectedStore.admin_path || `admin/${selectedStore.domain}`}/dashboard`, '_blank')}
                  className="btn-primary flex-1"
                >
                  <ExternalLink size={18} className="mr-2" />
                  Ir al Panel
                </button>
                <button
                  onClick={() => loadStoreProducts(selectedStore.id)}
                  className="btn-secondary flex-1"
                  data-testid="view-products-btn"
                >
                  <Package size={18} className="mr-2" />
                  Ver Productos
                </button>
                {isSuperAdmin && (
                  <button
                    onClick={() => handleToggleActive(selectedStore.id)}
                    className="flex-1 px-4 py-2 rounded-lg font-medium text-white transition-colors"
                    style={{ background: selectedStore.is_active !== false ? '#dc2626' : '#22c55e' }}
                    data-testid="toggle-store-btn"
                  >
                    {selectedStore.is_active !== false ? (
                      <><Ban size={18} className="inline mr-2" />Inhabilitar</>
                    ) : (
                      <><CheckCircle size={18} className="inline mr-2" />Habilitar</>
                    )}
                  </button>
                )}
              </div>

              {/* Products Section */}
              {showProducts && (
                <div className="border-t pt-6 mt-6" style={{ borderColor: 'var(--border-light)' }}>
                  <h3 className="font-bold flex items-center gap-2 mb-4">
                    <Package size={20} className="text-accent-text" />
                    Productos ({storeProducts.length})
                  </h3>
                  {storeProducts.length === 0 ? (
                    <p className="text-text-muted text-center py-4">No hay productos</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {storeProducts.map(product => (
                        <div 
                          key={product.id}
                          className="flex items-center justify-between p-3 rounded-lg border"
                          style={{ borderColor: 'var(--border-light)' }}
                        >
                          {editingProduct?.id === product.id ? (
                            <div className="flex-1 grid grid-cols-4 gap-2 mr-2">
                              <input value={productForm.name} onChange={e => setProductForm({...productForm, name: e.target.value})}
                                className="px-2 py-1 border rounded text-sm" placeholder="Nombre" />
                              <input type="number" step="0.01" value={productForm.price} onChange={e => setProductForm({...productForm, price: e.target.value})}
                                className="px-2 py-1 border rounded text-sm" placeholder="Precio" />
                              <input type="number" value={productForm.stock} onChange={e => setProductForm({...productForm, stock: e.target.value})}
                                className="px-2 py-1 border rounded text-sm" placeholder="Stock" />
                              <input type="number" step="0.01" value={productForm.cost_price} onChange={e => setProductForm({...productForm, cost_price: e.target.value})}
                                className="px-2 py-1 border rounded text-sm" placeholder="Costo" />
                            </div>
                          ) : (
                            <div className="flex-1">
                              <p className="font-medium text-sm">{product.name}</p>
                              <p className="text-xs text-text-muted">
                                Precio: ${product.price?.toFixed(2)} | Stock: {product.stock || 0} | Costo: ${(product.cost_price || 0).toFixed(2)}
                              </p>
                            </div>
                          )}
                          <div className="flex gap-1">
                            {editingProduct?.id === product.id ? (
                              <>
                                <button onClick={handleSaveProduct} className="p-1.5 bg-green-50 hover:bg-green-100 rounded text-green-600 text-xs font-medium px-2">
                                  Guardar
                                </button>
                                <button onClick={() => setEditingProduct(null)} className="p-1.5 bg-gray-50 hover:bg-gray-100 rounded text-gray-600 text-xs font-medium px-2">
                                  Cancelar
                                </button>
                              </>
                            ) : (
                              <>
                                <button onClick={() => handleEditProduct(product)} className="p-1.5 hover:bg-accent-wash rounded" title="Editar">
                                  <Edit size={14} className="text-accent-text" />
                                </button>
                                <button onClick={() => handleDeleteProduct(product.id, product.name)} className="p-1.5 hover:bg-red-50 rounded" title="Eliminar">
                                  <Trash2 size={14} className="text-red-500" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Admin Modal */}
      {showAdminModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b flex justify-between items-center" style={{ borderColor: 'var(--border-light)' }}>
              <h2 className="heading-4">
                {editingAdmin ? 'Editar Admin' : 'Nuevo Admin'}
              </h2>
              <button onClick={() => setShowAdminModal(false)} className="p-2 hover:bg-section rounded-lg">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleAdminSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Usuario *</label>
                <div className="relative">
                  <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                  <input
                    type="text"
                    value={adminFormData.username}
                    onChange={(e) => { setAdminFormData({...adminFormData, username: e.target.value}); setSaUsernameAvailable(null); }}
                    onBlur={() => checkSaUsername(adminFormData.username)}
                    className="w-full pl-10 pr-10 py-2 rounded-lg border"
                    style={{ borderColor: saUsernameAvailable === false ? '#ef4444' : saUsernameAvailable === true ? '#22c55e' : 'var(--border-light)', transition: 'border-color 0.2s' }}
                    placeholder="nombre_usuario"
                    required
                  />
                  {saUsernameAvailable === true && <CheckCircle size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500" />}
                  {saUsernameAvailable === false && <AlertCircle size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500" />}
                </div>
                {saCheckingUsername && <p className="text-xs text-blue-500 mt-1">Verificando disponibilidad...</p>}
                {saUsernameAvailable === false && <p className="text-xs text-red-500 mt-1">Ese usuario no está disponible, elige otro</p>}
                {saUsernameAvailable === true && <p className="text-xs text-green-500 mt-1">Usuario disponible</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Contraseña {editingAdmin ? '(dejar vacío para mantener)' : '*'}
                </label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                  <input
                    type="password"
                    value={adminFormData.password}
                    onChange={(e) => setAdminFormData({...adminFormData, password: e.target.value})}
                    className="w-full pl-10 pr-4 py-2 rounded-lg border"
                    style={{ borderColor: 'var(--border-light)' }}
                    placeholder="••••••••"
                    required={!editingAdmin}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Rol</label>
                <select
                  value={adminFormData.role}
                  onChange={(e) => setAdminFormData({...adminFormData, role: e.target.value})}
                  className="w-full px-4 py-2 rounded-lg border"
                  style={{ borderColor: 'var(--border-light)' }}
                  data-testid="admin-role-select"
                >
                  <option value="admin">Administrador (Control total)</option>
                  <option value="empleado">Empleado (Ventas y pedidos)</option>
                  <option value="inventario">Inventario (Productos y categorías)</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowAdminModal(false)} className="btn-secondary flex-1">
                  Cancelar
                </button>
                <button type="submit" className="btn-primary flex-1">
                  {editingAdmin ? 'Guardar' : 'Crear Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setDeleteConfirm(null)} />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full" data-testid="sa-delete-confirm-modal">
              <div className="text-center mb-4">
                <AlertTriangle size={40} className="mx-auto text-red-500 mb-3" />
                <h3 className="font-bold text-lg">
                  {deleteConfirm.type === 'store' ? 'Eliminar Tienda' : 
                   deleteConfirm.type === 'product' ? 'Eliminar Producto' : 'Eliminar Admin'}
                </h3>
                <p className="text-text-muted mt-2">
                  {deleteConfirm.name 
                    ? <>¿Eliminar <strong>"{deleteConfirm.name}"</strong>? Esta acción no se puede deshacer.</>
                    : '¿Estás seguro de eliminar este elemento?'
                  }
                </p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setDeleteConfirm(null)} className="btn-secondary flex-1">
                  Cancelar
                </button>
                <button onClick={confirmDeleteAction}
                  className="flex-1 px-4 py-2 rounded-lg text-white font-medium"
                  style={{ background: '#ef4444' }}
                  data-testid="sa-delete-confirm-btn"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Landing Page Editor Modal */}
      {showLandingEditor && landingConfig && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b flex justify-between items-center" style={{ borderColor: 'var(--border-light)', background: 'var(--accent-wash)' }}>
              <div className="flex items-center gap-3">
                <FileEdit size={24} className="text-accent-text" />
                <div>
                  <h2 className="heading-4">Editar Página Principal</h2>
                  <p className="text-sm text-text-muted">Personaliza tu landing page</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={handleSaveLanding} disabled={savingLanding}
                  className="btn-primary" data-testid="save-landing-btn">
                  <Save size={18} className="mr-2" />
                  {savingLanding ? 'Guardando...' : 'Guardar'}
                </button>
                <button onClick={() => setShowLandingEditor(false)} className="p-2 hover:bg-white rounded-lg">
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Brand & Hero */}
              <div className="bg-white rounded-xl border p-5" style={{ borderColor: 'var(--border-light)' }}>
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <Globe size={18} className="text-accent-text" />
                  Marca y Hero
                </h3>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">Nombre de la Marca</label>
                      <input type="text" value={landingConfig.brand_name}
                        onChange={e => setLandingConfig({ ...landingConfig, brand_name: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }}
                        data-testid="landing-brand-name" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Número WhatsApp</label>
                      <input type="text" value={landingConfig.whatsapp_number}
                        onChange={e => setLandingConfig({ ...landingConfig, whatsapp_number: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }}
                        placeholder="593999999999" data-testid="landing-whatsapp" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Badge del Hero</label>
                    <input type="text" value={landingConfig.hero_badge}
                      onChange={e => setLandingConfig({ ...landingConfig, hero_badge: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }}
                      data-testid="landing-hero-badge" />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">Título (prefijo)</label>
                      <input type="text" value={landingConfig.hero_title_prefix}
                        onChange={e => setLandingConfig({ ...landingConfig, hero_title_prefix: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Título (resaltado verde)</label>
                      <input type="text" value={landingConfig.hero_title_highlight}
                        onChange={e => setLandingConfig({ ...landingConfig, hero_title_highlight: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)', color: '#16a34a' }} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Título (sufijo)</label>
                      <input type="text" value={landingConfig.hero_title_suffix}
                        onChange={e => setLandingConfig({ ...landingConfig, hero_title_suffix: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Subtítulo</label>
                    <textarea value={landingConfig.hero_subtitle} rows={2}
                      onChange={e => setLandingConfig({ ...landingConfig, hero_subtitle: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }}
                      data-testid="landing-hero-subtitle" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Texto del Botón CTA</label>
                    <input type="text" value={landingConfig.cta_text}
                      onChange={e => setLandingConfig({ ...landingConfig, cta_text: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} />
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="bg-white rounded-xl border p-5" style={{ borderColor: 'var(--border-light)' }}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold flex items-center gap-2">
                    <Package size={18} className="text-accent-text" />
                    Funciones ({landingConfig.features.length})
                  </h3>
                  <button type="button" onClick={addFeature} className="text-sm px-3 py-1 rounded-lg bg-accent-wash text-accent-text font-medium"
                    data-testid="add-feature-btn">
                    <Plus size={14} className="inline mr-1" />Agregar
                  </button>
                </div>
                <div className="space-y-3">
                  {landingConfig.features.map((f, i) => (
                    <div key={`feat-${i}-${f.title}`} className="flex gap-3 items-start p-3 rounded-lg border" style={{ borderColor: 'var(--border-light)' }}>
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <input type="text" value={f.title} placeholder="Título"
                          onChange={e => updateFeature(i, 'title', e.target.value)}
                          className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }}
                          data-testid={`feature-title-${i}`} />
                        <input type="text" value={f.desc} placeholder="Descripción"
                          onChange={e => updateFeature(i, 'desc', e.target.value)}
                          className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} />
                      </div>
                      <button onClick={() => removeFeature(i)} className="p-1.5 hover:bg-red-50 rounded text-red-500 mt-1">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Plans */}
              <div className="bg-white rounded-xl border p-5" style={{ borderColor: 'var(--border-light)' }}>
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <DollarSign size={18} className="text-accent-text" />
                  Planes ({landingConfig.plans.length})
                </h3>
                <div className="space-y-3">
                  {landingConfig.plans.map((plan, pi) => (
                    <div key={`plan-${pi}-${plan.name}`} className="border rounded-xl overflow-hidden" style={{ borderColor: plan.highlight ? '#16a34a' : 'var(--border-light)' }}>
                      <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                        onClick={() => setExpandedPlan(expandedPlan === pi ? null : pi)}>
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-sm">{plan.name || 'Plan sin nombre'}</span>
                          <span className="text-sm text-accent-text font-bold">{plan.price}</span>
                          {plan.highlight && <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Popular</span>}
                        </div>
                        {expandedPlan === pi ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </div>
                      {expandedPlan === pi && (
                        <div className="p-3 border-t space-y-3" style={{ borderColor: 'var(--border-light)' }}>
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <label className="block text-xs font-medium mb-1">Nombre</label>
                              <input type="text" value={plan.name}
                                onChange={e => updatePlan(pi, 'name', e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }}
                                data-testid={`plan-name-${pi}`} />
                            </div>
                            <div>
                              <label className="block text-xs font-medium mb-1">Precio</label>
                              <input type="text" value={plan.price}
                                onChange={e => updatePlan(pi, 'price', e.target.value)}
                                className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }}
                                data-testid={`plan-price-${pi}`} />
                            </div>
                            <div className="flex items-end">
                              <label className="flex items-center gap-2 px-3 py-2 cursor-pointer">
                                <input type="checkbox" checked={plan.highlight}
                                  onChange={e => updatePlan(pi, 'highlight', e.target.checked)} />
                                <span className="text-sm">Destacado</span>
                              </label>
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-xs font-medium">Características</label>
                              <button type="button" onClick={() => addPlanFeature(pi)} className="text-xs text-accent-text font-medium">
                                + Agregar
                              </button>
                            </div>
                            <div className="space-y-1">
                              {plan.features.map((feat, fi) => (
                                <div key={`planfeat-${pi}-${fi}`} className="flex gap-2">
                                  <input type="text" value={feat}
                                    onChange={e => updatePlanFeature(pi, fi, e.target.value)}
                                    className="flex-1 px-3 py-1.5 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} />
                                  <button onClick={() => removePlanFeature(pi, fi)} className="p-1 text-red-400 hover:text-red-600">
                                    <X size={14} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact & Footer */}
              <div className="bg-white rounded-xl border p-5" style={{ borderColor: 'var(--border-light)' }}>
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <Settings size={18} className="text-accent-text" />
                  Contacto y Footer
                </h3>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">Título de Contacto</label>
                      <input type="text" value={landingConfig.contact_title}
                        onChange={e => setLandingConfig({ ...landingConfig, contact_title: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Tagline del Footer</label>
                      <input type="text" value={landingConfig.footer_tagline}
                        onChange={e => setLandingConfig({ ...landingConfig, footer_tagline: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Subtítulo de Contacto</label>
                    <input type="text" value={landingConfig.contact_subtitle}
                      onChange={e => setLandingConfig({ ...landingConfig, contact_subtitle: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Sticky Save Footer */}
            <div className="p-4 border-t flex justify-between items-center" style={{ borderColor: 'var(--border-light)' }}>
              <a href="/" target="_blank" rel="noopener noreferrer" className="text-sm text-accent-text font-medium flex items-center gap-1">
                <ExternalLink size={14} /> Ver Landing
              </a>
              <button onClick={handleSaveLanding} disabled={savingLanding}
                className="btn-primary" data-testid="save-landing-btn-footer">
                <Save size={18} className="mr-2" />
                {savingLanding ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Super Admin Chat Modal */}
      {showChat && chatStore && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50 md:p-4" onClick={e => e.target === e.currentTarget && setShowChat(false)}>
          <div className="bg-white rounded-t-xl md:rounded-xl w-full md:max-w-lg flex flex-col" style={{ height: '85vh', maxHeight: '85vh' }}>
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-light)' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-100">
                  <Store size={20} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold">{chatStore.store_name}</h3>
                  <p className="text-xs text-text-muted">{chatStore.domain} - {chatStore.owner_email}</p>
                </div>
              </div>
              <button onClick={() => setShowChat(false)} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="close-sa-chat">
                <X size={20} />
              </button>
            </div>

            {/* Quick messages */}
            <div className="px-4 py-2 border-b flex gap-2 overflow-x-auto" style={{ borderColor: 'var(--border-light)' }}>
              {[
                'Recordatorio: Pago pendiente',
                'Tu tienda sera inhabilitada por falta de pago',
                'Mantenimiento programado'
              ].map((quick, i) => (
                <button key={i} onClick={() => setChatInput(quick)}
                  className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border hover:bg-gray-50"
                  style={{ borderColor: 'var(--border-light)' }}
                  data-testid={`quick-msg-${i}`}
                >
                  {quick}
                </button>
              ))}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: '#f8f9fb' }}>
              {chatMessages.length === 0 && (
                <div className="text-center text-text-muted text-sm py-8">
                  <MessageCircle size={40} className="mx-auto mb-3 text-gray-300" />
                  Envía el primer mensaje a esta tienda
                </div>
              )}
              {chatMessages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.from_superadmin ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${
                      msg.from_superadmin
                        ? 'text-white'
                        : 'bg-white border text-gray-800'
                    }`}
                    style={msg.from_superadmin ? { background: '#0078D4' } : { borderColor: 'var(--border-light)' }}
                  >
                    <p>{msg.message}</p>
                    <p className={`text-[10px] mt-1 ${msg.from_superadmin ? 'text-white/70' : 'text-gray-400'}`}>
                      {new Date(typeof msg.timestamp === 'string' && !msg.timestamp.endsWith('Z') ? msg.timestamp + 'Z' : msg.timestamp).toLocaleString('es-EC', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short', timeZone: 'America/Guayaquil' })}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={sendChatMessage} className="p-3 border-t flex gap-2" style={{ borderColor: 'var(--border-light)' }}>
              <input
                type="text"
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                placeholder="Escribe un mensaje..."
                className="flex-1 px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: 'var(--border-light)' }}
                data-testid="sa-chat-input"
              />
              <button
                type="submit"
                disabled={chatSending || !chatInput.trim()}
                className="px-4 py-2.5 rounded-xl text-white font-medium text-sm disabled:opacity-50"
                style={{ background: '#0078D4' }}
                data-testid="sa-chat-send"
              >
                <Send size={18} />
              </button>
            </form>
          </div>
        </div>
      )}
      {/* Super Admin Users Modal */}
      {showSaUsers && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center" style={{ borderColor: 'var(--border-light)' }}>
              <h2 className="font-bold text-lg flex items-center gap-2">
                <Users size={20} className="text-accent-text" /> Usuarios del Panel
              </h2>
              <button onClick={() => setShowSaUsers(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              {/* Create new user form */}
              <form onSubmit={handleCreateSaUser} className="space-y-3 p-4 rounded-xl border" style={{ borderColor: 'var(--border-light)', background: 'var(--bg-section)' }}>
                <h3 className="font-medium text-sm">Crear nuevo usuario</h3>
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" placeholder="Usuario" value={saUserForm.username}
                    onChange={e => setSaUserForm({...saUserForm, username: e.target.value})}
                    className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }}
                    data-testid="sa-new-user-username" required />
                  <input type="text" placeholder="Contraseña" value={saUserForm.password}
                    onChange={e => setSaUserForm({...saUserForm, password: e.target.value})}
                    className="px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }}
                    data-testid="sa-new-user-password" required />
                </div>
                {/* Store selector for new user */}
                {stores.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-text-muted mb-2">Tiendas asignadas (dejar vacío = ninguna):</p>
                    <div className="flex flex-wrap gap-2">
                      {stores.map(s => (
                        <button key={s.id} type="button"
                          onClick={() => {
                            const cur = saUserForm.allowed_stores || [];
                            const updated = cur.includes(s.id) ? cur.filter(x => x !== s.id) : [...cur, s.id];
                            setSaUserForm({...saUserForm, allowed_stores: updated});
                          }}
                          className="px-3 py-1.5 rounded-lg border text-xs font-medium transition-all"
                          style={{
                            borderColor: (saUserForm.allowed_stores || []).includes(s.id) ? 'var(--accent-primary)' : 'var(--border-light)',
                            background: (saUserForm.allowed_stores || []).includes(s.id) ? 'var(--accent-wash)' : 'white',
                            color: (saUserForm.allowed_stores || []).includes(s.id) ? 'var(--accent-text)' : 'var(--text-muted)'
                          }}>
                          {(saUserForm.allowed_stores || []).includes(s.id) ? '✓ ' : ''}{s.store_name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <button type="submit" disabled={creatingSaUser} className="btn-primary text-sm w-full" data-testid="sa-create-user-btn">
                  <Plus size={16} className="mr-1" /> {creatingSaUser ? 'Creando...' : 'Crear Usuario'}
                </button>
              </form>

              {/* Users list */}
              <div className="space-y-3">
                <h3 className="font-medium text-sm text-text-muted">Usuarios creados</h3>
                {saUsers.length === 0 ? (
                  <p className="text-sm text-text-muted text-center py-4">No hay usuarios adicionales</p>
                ) : saUsers.map(u => (
                  <div key={u.id} className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border-light)' }}>
                    <div className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                          <User size={18} className="text-blue-500" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{u.username}</p>
                          <p className="text-xs text-text-muted">
                            {u.role === 'control_ventas' ? 'Control de Ventas' : u.role}
                          </p>
                        </div>
                        {/* Show password */}
                        <div className="ml-2 px-3 py-1 rounded-lg bg-gray-50 border" style={{ borderColor: 'var(--border-light)' }}>
                          <p className="text-[10px] text-text-muted">Contraseña</p>
                          <p className="text-sm font-mono font-medium">{u.plain_password || '••••••'}</p>
                        </div>
                        {u.created_at && (
                          <span className="text-[10px] text-text-muted">{formatDate(u.created_at)}</span>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => setEditingSaStores(editingSaStores === u.id ? null : u.id)}
                          className="p-2 hover:bg-blue-50 rounded-lg" title="Configurar tiendas">
                          <Store size={16} className="text-blue-500" />
                        </button>
                        <button onClick={() => handleDeleteSaUser(u.id, u.username)}
                          className="p-2 hover:bg-red-50 rounded-lg" title="Eliminar">
                          <Trash2 size={16} className="text-red-500" />
                        </button>
                      </div>
                    </div>
                    {/* Assigned stores display */}
                    <div className="px-4 pb-2">
                      <div className="flex flex-wrap gap-1">
                        {(u.allowed_stores || []).length === 0 
                          ? <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">Sin tiendas asignadas</span>
                          : (u.allowed_stores || []).map(sid => {
                              const store = stores.find(s => s.id === sid);
                              return <span key={sid} className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">{store?.store_name || sid.slice(0,8)}</span>;
                            })
                        }
                      </div>
                    </div>
                    {/* Inline store editor */}
                    {editingSaStores === u.id && (
                      <div className="p-4 border-t" style={{ borderColor: 'var(--border-light)', background: 'var(--bg-section)' }}>
                        <p className="text-xs font-medium mb-2">Selecciona las tiendas que puede ver:</p>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {stores.map(s => (
                            <button key={s.id} type="button" onClick={() => toggleStoreForUser(s.id)}
                              className="px-3 py-1.5 rounded-lg border text-xs font-medium transition-all"
                              style={{
                                borderColor: (u.allowed_stores || []).includes(s.id) ? 'var(--accent-primary)' : 'var(--border-light)',
                                background: (u.allowed_stores || []).includes(s.id) ? 'var(--accent-wash)' : 'white',
                                color: (u.allowed_stores || []).includes(s.id) ? 'var(--accent-text)' : 'var(--text-muted)'
                              }}>
                              {(u.allowed_stores || []).includes(s.id) ? '✓ ' : ''}{s.store_name}
                            </button>
                          ))}
                        </div>
                        <button onClick={() => saveUserStores(u.id)} className="btn-primary text-xs px-4 py-2">
                          <Save size={14} className="mr-1" /> Guardar tiendas
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Permissions info */}
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                <p className="text-xs font-medium text-blue-800 mb-1">Permisos de "Control de Ventas":</p>
                <ul className="text-xs text-blue-700 space-y-0.5">
                  <li>• Ver solo las tiendas asignadas</li>
                  <li>• Ver estadísticas y crear cuentas en sus tiendas</li>
                  <li>• Escribir como soporte técnico</li>
                  <li>• NO puede editar landing page</li>
                  <li>• NO puede crear/eliminar tiendas</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminPanel;
